import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api';
import { Btn, Empty, ErrorBox, Loading, Modal, useAction } from '../ui';

type Names = { uz: string; kaa: string; ru: string };
interface Attr {
  id: string; key: string; type: string; isRequired: boolean; isFilterable: boolean; labels: Names;
  options: { value: string; labels: Names }[] | null; minValue: number | null; maxValue: number | null; unit: string | null; sortOrder: number;
}
interface Cat {
  id: string; parentId: string | null; slug: string; icon: string | null; sortOrder: number; isActive: boolean; hasCondition: boolean;
  names: Partial<Names>; listings: number; attributes: Attr[];
}

function NamesEditor({ value, onChange }: { value: Partial<Names>; onChange: (v: Names) => void }) {
  const v = { uz: value.uz ?? '', kaa: value.kaa ?? '', ru: value.ru ?? '' };
  return (
    <div className="names">
      {(['uz', 'kaa', 'ru'] as const).map((l) => (
        <label key={l} className="field">
          {l === 'uz' ? "O'zbekcha" : l === 'kaa' ? 'Qaraqalpaqsha' : 'Русский'}
          <input value={v[l]} maxLength={100} onChange={(e) => onChange({ ...v, [l]: e.target.value })} />
        </label>
      ))}
    </div>
  );
}

export function CategoriesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['admin-categories'], queryFn: () => api<{ items: Cat[] }>('/admin/categories').then((r) => r.items) });
  const [edit, setEdit] = useState<Cat | 'new' | null>(null);
  const [attrsOf, setAttrsOf] = useState<Cat | null>(null);
  const tree = useMemo(() => {
    const items = data ?? [];
    const roots = items.filter((c) => !c.parentId).sort((a, b) => a.sortOrder - b.sortOrder);
    return roots.map((r) => ({ root: r, children: items.filter((c) => c.parentId === r.id).sort((a, b) => a.sortOrder - b.sortOrder) }));
  }, [data]);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['admin-categories'] });
  if (isLoading) return <Loading />;
  if (error) return <ErrorBox error={error} onRetry={() => void refetch()} />;
  const row = (c: Cat, child: boolean) => (
    <tr key={c.id} className={c.isActive ? '' : 'inactive'}>
      <td style={{ paddingLeft: child ? 28 : 8 }}>{child ? '└ ' : ''}<strong>{c.names.ru}</strong><div className="muted">{c.names.uz} · {c.names.kaa}</div></td>
      <td><code>{c.slug}</code></td><td>{c.sortOrder}</td><td>{c.isActive ? 'да' : 'скрыта'}</td><td>{c.listings}</td>
      <td>{c.attributes.length}</td>
      <td className="row"><Btn kind="secondary" onClick={() => setEdit(c)}>Изменить</Btn><Btn kind="ghost" onClick={() => setAttrsOf(c)}>Характеристики</Btn></td>
    </tr>
  );
  return (
    <div>
      <h1>Категории</h1>
      <div className="toolbar"><Btn onClick={() => setEdit('new')}>Новая категория</Btn></div>
      <table>
        <thead><tr><th>Название</th><th>Slug</th><th>Порядок</th><th>Видна</th><th>Объявлений</th><th>Хар-к</th><th /></tr></thead>
        <tbody>{tree.flatMap(({ root, children }) => [row(root, false), ...children.map((c) => row(c, true))])}</tbody>
      </table>
      {edit ? <CategoryForm cat={edit === 'new' ? null : edit} all={data ?? []} onClose={() => { setEdit(null); refresh(); }} /> : null}
      {attrsOf ? <AttributesEditor cat={(data ?? []).find((c) => c.id === attrsOf.id) ?? attrsOf} onChanged={refresh} onClose={() => setAttrsOf(null)} /> : null}
    </div>
  );
}

function CategoryForm({ cat, all, onClose }: { cat: Cat | null; all: Cat[]; onClose: () => void }) {
  const action = useAction();
  const [f, setF] = useState({
    slug: cat?.slug ?? '', parentId: cat?.parentId ?? '', icon: cat?.icon ?? '', sortOrder: cat?.sortOrder ?? 0,
    isActive: cat?.isActive ?? true, hasCondition: cat?.hasCondition ?? true, names: { uz: cat?.names.uz ?? '', kaa: cat?.names.kaa ?? '', ru: cat?.names.ru ?? '' },
  });
  const save = () =>
    action.run(async () => {
      const body = { ...f, parentId: f.parentId || null, icon: f.icon || null };
      if (cat) await api(`/admin/categories/${cat.id}`, { method: 'PATCH', json: body });
      else await api('/admin/categories', { method: 'POST', json: body });
      onClose();
    });
  return (
    <Modal title={cat ? `Категория: ${cat.names.ru}` : 'Новая категория'} onClose={onClose}>
      <NamesEditor value={f.names} onChange={(names) => setF({ ...f, names })} />
      <label className="field">Slug (латиница, цифры, дефис)<input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} /></label>
      <label className="field">Родитель
        <select value={f.parentId} onChange={(e) => setF({ ...f, parentId: e.target.value })}>
          <option value="">— корневая —</option>
          {all.filter((c) => !c.parentId && c.id !== cat?.id).map((c) => <option key={c.id} value={c.id}>{c.names.ru}</option>)}
        </select>
      </label>
      <label className="field">Иконка (car, home, smartphone, sofa, briefcase, wrench, shirt, baby, paw, wheat, factory, gift)<input value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} /></label>
      <label className="field">Порядок<input type="number" value={f.sortOrder} onChange={(e) => setF({ ...f, sortOrder: Number(e.target.value) })} /></label>
      <label className="check"><input type="checkbox" checked={f.isActive} onChange={(e) => setF({ ...f, isActive: e.target.checked })} /> Показывать пользователям</label>
      <label className="check"><input type="checkbox" checked={f.hasCondition} onChange={(e) => setF({ ...f, hasCondition: e.target.checked })} /> Есть поле «Состояние» (новое/б/у)</label>
      <div className="row">
        <Btn busy={action.busy} onClick={() => void save()}>Сохранить</Btn>
        {cat && cat.listings === 0 ? <Btn kind="danger" busy={action.busy} onClick={() => void action.run(async () => { if (window.confirm('Удалить категорию?')) { await api(`/admin/categories/${cat.id}`, { method: 'DELETE' }); onClose(); } })}>Удалить</Btn> : null}
      </div>
      {action.error ? <ErrorBox error={action.error} /> : null}
    </Modal>
  );
}

function AttributesEditor({ cat, onChanged, onClose }: { cat: Cat; onChanged: () => void; onClose: () => void }) {
  const action = useAction();
  const empty = { key: '', type: 'text', isRequired: false, isFilterable: false, labels: { uz: '', kaa: '', ru: '' }, optionsText: '', minValue: '', maxValue: '', unit: '', sortOrder: 0 };
  const [f, setF] = useState(empty);
  // Варианты select: по строке на вариант «value | uz | kaa | ru».
  const parseOptions = (t: string) =>
    t.split('\n').map((s) => s.trim()).filter(Boolean).map((line) => {
      const [value, uz, kaa, ru] = line.split('|').map((x) => x.trim());
      return { value: value!, labels: { uz: uz || value!, kaa: kaa || uz || value!, ru: ru || value! } };
    });
  return (
    <Modal title={`Характеристики: ${cat.names.ru}`} onClose={onClose}>
      <p className="muted">Характеристики наследуются подкатегориями. Ключ и тип после создания не меняются.</p>
      {cat.attributes.length ? (
        <table>
          <thead><tr><th>Ключ</th><th>Тип</th><th>Название</th><th>Обяз.</th><th>Фильтр</th><th /></tr></thead>
          <tbody>
            {cat.attributes.map((a) => (
              <tr key={a.id}>
                <td><code>{a.key}</code></td><td>{a.type}</td><td>{a.labels.ru}{a.unit ? `, ${a.unit}` : ''}</td>
                <td><input type="checkbox" checked={a.isRequired} onChange={(e) => void action.run(async () => { await api(`/admin/attributes/${a.id}`, { method: 'PATCH', json: { isRequired: e.target.checked } }); onChanged(); })} /></td>
                <td><input type="checkbox" checked={a.isFilterable} onChange={(e) => void action.run(async () => { await api(`/admin/attributes/${a.id}`, { method: 'PATCH', json: { isFilterable: e.target.checked } }); onChanged(); })} /></td>
                <td><Btn kind="danger" onClick={() => void action.run(async () => { if (window.confirm('Удалить характеристику и её значения во всех объявлениях?')) { await api(`/admin/attributes/${a.id}`, { method: 'DELETE' }); onChanged(); } })}>✕</Btn></td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <Empty text="Нет собственных характеристик" />}
      <h3>Добавить</h3>
      <div className="grid2">
        <label className="field">Ключ (латиница, a-z0-9_)<input value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} /></label>
        <label className="field">Тип<select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}><option value="text">Текст</option><option value="number">Число</option><option value="select">Список</option><option value="boolean">Да/нет</option></select></label>
      </div>
      <NamesEditor value={f.labels} onChange={(labels) => setF({ ...f, labels })} />
      {f.type === 'number' ? (
        <div className="grid2">
          <label className="field">Мин.<input value={f.minValue} onChange={(e) => setF({ ...f, minValue: e.target.value })} /></label>
          <label className="field">Макс.<input value={f.maxValue} onChange={(e) => setF({ ...f, maxValue: e.target.value })} /></label>
          <label className="field">Единица (km, m², GB…)<input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></label>
        </div>
      ) : null}
      {f.type === 'select' ? <label className="field">Варианты: по строке «value | uz | kaa | ru»<textarea rows={5} value={f.optionsText} onChange={(e) => setF({ ...f, optionsText: e.target.value })} /></label> : null}
      <label className="check"><input type="checkbox" checked={f.isRequired} onChange={(e) => setF({ ...f, isRequired: e.target.checked })} /> Обязательная</label>
      <label className="check"><input type="checkbox" checked={f.isFilterable} onChange={(e) => setF({ ...f, isFilterable: e.target.checked })} /> Показывать в фильтрах</label>
      <Btn busy={action.busy} onClick={() => void action.run(async () => {
        await api(`/admin/categories/${cat.id}/attributes`, { method: 'POST', json: {
          key: f.key, type: f.type, isRequired: f.isRequired, isFilterable: f.isFilterable, labels: f.labels, sortOrder: cat.attributes.length + 1,
          options: f.type === 'select' ? parseOptions(f.optionsText) : null,
          minValue: f.minValue === '' ? null : Number(f.minValue), maxValue: f.maxValue === '' ? null : Number(f.maxValue), unit: f.unit || null,
        } });
        setF(empty);
        onChanged();
      })}>Добавить характеристику</Btn>
      {action.error ? <ErrorBox error={action.error} /> : null}
    </Modal>
  );
}

interface Loc { id: string; parentId: string | null; type: string; slug: string; nameUz: string; nameKaa: string; nameRu: string; sortOrder: number; isActive: boolean; isVerified: boolean }

export function LocationsPage() {
  const qc = useQueryClient();
  const action = useAction();
  const { data, isLoading, error } = useQuery({ queryKey: ['admin-locations'], queryFn: () => api<{ items: Loc[] }>('/admin/locations').then((r) => r.items) });
  const [form, setForm] = useState<{ slug: string; parentId: string; type: string; names: Names } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['admin-locations'] });
  const byId = Object.fromEntries((data ?? []).map((l) => [l.id, l]));
  const depth = (l: Loc) => { let d = 0, cur = l; while (cur.parentId && byId[cur.parentId] && d < 6) { d++; cur = byId[cur.parentId]!; } return d; };
  const ordered = useMemo(() => {
    const items = data ?? [];
    const out: Loc[] = [];
    const walk = (pid: string | null) => items.filter((l) => l.parentId === pid).sort((a, b) => a.sortOrder - b.sortOrder).forEach((l) => { out.push(l); walk(l.id); });
    walk(null);
    return out;
  }, [data]);
  if (isLoading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const unverified = (data ?? []).filter((l) => !l.isVerified).length;
  return (
    <div>
      <h1>География</h1>
      {unverified ? <p className="warn">Непроверенных записей: {unverified}. Начальный справочник не является официальным перечнем — сверьте названия и отметьте проверенные.</p> : null}
      <div className="toolbar"><Btn onClick={() => setForm({ slug: '', parentId: '', type: 'settlement', names: { uz: '', kaa: '', ru: '' } })}>Добавить</Btn></div>
      {action.error ? <ErrorBox error={action.error} /> : null}
      <table>
        <thead><tr><th>Название</th><th>Тип</th><th>Видно</th><th>Проверено</th></tr></thead>
        <tbody>
          {ordered.map((l) => (
            <tr key={l.id} className={l.isActive ? '' : 'inactive'}>
              <td style={{ paddingLeft: 8 + depth(l) * 20 }}><strong>{l.nameRu}</strong><div className="muted">{l.nameUz} · {l.nameKaa}</div></td>
              <td>{l.type}</td>
              <td><input type="checkbox" checked={l.isActive} onChange={(e) => void action.run(async () => { await api(`/admin/locations/${l.id}`, { method: 'PATCH', json: { isActive: e.target.checked } }); refresh(); })} /></td>
              <td><input type="checkbox" checked={l.isVerified} onChange={(e) => void action.run(async () => { await api(`/admin/locations/${l.id}`, { method: 'PATCH', json: { isVerified: e.target.checked } }); refresh(); })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {form ? (
        <Modal title="Новое место" onClose={() => setForm(null)}>
          <NamesEditor value={form.names} onChange={(names) => setForm({ ...form, names })} />
          <label className="field">Slug<input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></label>
          <label className="field">Тип<select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>{['region', 'district', 'city', 'settlement'].map((t) => <option key={t}>{t}</option>)}</select></label>
          <label className="field">Входит в<select value={form.parentId} onChange={(e) => setForm({ ...form, parentId: e.target.value })}><option value="">—</option>{ordered.map((l) => <option key={l.id} value={l.id}>{'— '.repeat(depth(l))}{l.nameRu}</option>)}</select></label>
          <Btn busy={action.busy} onClick={() => void action.run(async () => { await api('/admin/locations', { method: 'POST', json: { ...form, parentId: form.parentId || null } }); setForm(null); refresh(); })}>Создать</Btn>
          {action.error ? <ErrorBox error={action.error} /> : null}
        </Modal>
      ) : null}
    </div>
  );
}

export function TranslationsPage() {
  const qc = useQueryClient();
  const action = useAction();
  const [locale, setLocale] = useState<'uz' | 'kaa' | 'ru'>('kaa');
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [needsReview, setNeedsReview] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['translations', locale],
    queryFn: () => api<{ items: { key: string; value: string; needsReview: boolean; updatedAt: string }[] }>(`/admin/translations/${locale}`).then((r) => r.items),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['translations', locale] });
  return (
    <div>
      <h1>Переводы интерфейса</h1>
      <p className="muted">Здесь задаются исправления строк Mini App поверх файлов локализации (ключи — из apps/miniapp/src/i18n/locales, например <code>home.fresh</code>). Изменения применяются без пересборки.</p>
      <div className="toolbar">
        <select value={locale} onChange={(e) => setLocale(e.target.value as typeof locale)}><option value="uz">O'zbekcha</option><option value="kaa">Qaraqalpaqsha</option><option value="ru">Русский</option></select>
      </div>
      <div className="grid2">
        <label className="field">Ключ<input value={key} onChange={(e) => setKey(e.target.value)} placeholder="sell.publish" /></label>
        <label className="field">Значение<input value={value} onChange={(e) => setValue(e.target.value)} /></label>
      </div>
      <label className="check"><input type="checkbox" checked={needsReview} onChange={(e) => setNeedsReview(e.target.checked)} /> Требует проверки носителем языка</label>
      <Btn busy={action.busy} disabled={!key || !value} onClick={() => void action.run(async () => { await api(`/admin/translations/${locale}/${encodeURIComponent(key)}`, { method: 'PUT', json: { value, needsReview } }); setKey(''); setValue(''); refresh(); })}>Сохранить</Btn>
      {action.error ? <ErrorBox error={action.error} /> : null}
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} /> : !data?.length ? <Empty text="Переопределений нет" /> : (
        <table>
          <thead><tr><th>Ключ</th><th>Значение</th><th>Проверка</th><th /></tr></thead>
          <tbody>
            {data.map((t) => (
              <tr key={t.key}>
                <td><code>{t.key}</code></td><td>{t.value}</td><td>{t.needsReview ? <span className="pill pill-warn">нужна</span> : 'ок'}</td>
                <td><Btn kind="ghost" onClick={() => { setKey(t.key); setValue(t.value); setNeedsReview(t.needsReview); }}>Изменить</Btn><Btn kind="danger" onClick={() => void action.run(async () => { await api(`/admin/translations/${locale}/${encodeURIComponent(t.key)}`, { method: 'DELETE' }); refresh(); })}>✕</Btn></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
